"use client";

import { useEffect, useRef, useState } from "react";
import { useTrialForm } from "@/components/providers/TrialFormProvider";

/**
 * Sandboxed, lazy, auto-resizing iframe for an author-pasted HTML report.
 *
 * - Loads by `src` (the article's report route — the document stays out of the
 *   page HTML/RSC and `loading="lazy"` really defers the bytes) or, for surfaces
 *   without a route (`/private`), inline as `srcdoc`. Exactly one is given.
 *
 * - `sandbox` WITHOUT `allow-same-origin`: the report runs as an opaque origin,
 *   so it cannot touch this page (cookies/DOM), and any in-report links that key
 *   off `document.baseURI`/referrer fall back to their own absolute defaults
 *   (e.g. platform.hudson-labs.com) instead of resolving to this marketing site.
 * - Height is reported by the tiny script injected in `lib/embed` via
 *   postMessage; we authenticate it by the iframe's own contentWindow + a
 *   per-content token (the sandbox origin is "null", so we can't check origin).
 * - `loading="lazy"` + a reserved initial height keep it cheap and low-CLS.
 */

// Auto-resize bounds. The floor is intentionally small so a short embed (e.g. a
// couple of pricing cards) hugs its content instead of being padded out to a
// full-report height; the ceiling guards a runaway / mis-measured report.
const MIN_EMBED_HEIGHT = 40;
const MAX_EMBED_HEIGHT = 100000;

export default function ReportEmbed({
  src,
  srcdoc,
  token,
  title,
  initialHeight = 1600,
  framed = true,
}: {
  src?: string;
  srcdoc?: string;
  token: string;
  title: string;
  initialHeight?: number;
  /** Draw the default card chrome (border/shadow/rounded) around the iframe.
   *  A ````embed-plain` fence sets this false so the embed renders seamlessly. */
  framed?: boolean;
}) {
  const ref = useRef<HTMLIFrameElement>(null);
  const { openTrialForm } = useTrialForm();
  // Clamp to the same bounds the message handler uses, so a bad prop can't set a
  // pathological initial height.
  const [height, setHeight] = useState(() =>
    Math.min(Math.max(Math.ceil(initialHeight), MIN_EMBED_HEIGHT), MAX_EMBED_HEIGHT),
  );

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const frame = ref.current;
      if (!frame || e.source !== frame.contentWindow) return;
      const data = e.data as { type?: string; token?: string; height?: number } | null;
      if (!data) return;
      // A paywall/CTA button inside the report asks the host to open the trial
      // modal (the iframe is sandboxed, so it can't open it itself).
      if (data.type === "hl-embed-cta") {
        openTrialForm();
        return;
      }
      if (data.type !== "hl-embed-height" || data.token !== token) return;
      if (typeof data.height !== "number" || !Number.isFinite(data.height)) return;
      const next = Math.min(Math.max(Math.ceil(data.height), MIN_EMBED_HEIGHT), MAX_EMBED_HEIGHT);
      setHeight((prev) => (Math.abs(prev - next) > 4 ? next : prev));
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [token, openTrialForm]);

  return (
    // Default: a subtle card frame (border/shadow/rounded) around the report.
    // `embed-plain` fences set framed=false to drop the chrome and render the
    // embed seamlessly in the article flow (e.g. small self-styled widgets).
    <div
      className={
        framed
          ? "not-prose overflow-hidden rounded-2xl border border-black/[0.08] bg-white shadow-sm"
          : "not-prose"
      }
    >
      <iframe
        ref={ref}
        title={title}
        {...(src ? { src } : { srcDoc: srcdoc })}
        loading="lazy"
        referrerPolicy="no-referrer"
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        style={{ width: "100%", height, border: "0", display: "block" }}
      />
    </div>
  );
}
